"use client";

import * as React from "react";
import { Lock, Crown, ArrowRight } from "lucide-react";
import { useMembershipGate } from "./MembershipGate";
import { PlanComparison } from "./PlanComparison";

/**
 * Shown in place of a Premium-only feature when a Basic member navigates to it.
 * A 🔒 lock + "Upgrade to Premium Membership" CTA, plus the Basic vs Premium
 * comparison chart so the member can see exactly what upgrading unlocks.
 */
export function FeatureLock({ label }: { label: string }) {
  const { openPricing, openPreferences, preferencesMissing } = useMembershipGate();

  return (
    <div className="max-w-2xl mx-auto py-8 lg:py-12">
      <div className="text-center">
        <span className="inline-flex w-16 h-16 rounded-2xl bg-lavender-100 text-cj-800 items-center justify-center mb-4">
          <Lock className="w-8 h-8" />
        </span>
        <p className="text-[12px] font-bold uppercase tracking-wider text-cj-800">Premium Feature</p>
        <h1 className="text-2xl lg:text-[1.7rem] font-extrabold text-navy-500 leading-tight mt-1">
          {label} is locked
        </h1>
        <p className="text-sm text-gray-500 mt-2 leading-relaxed">
          {label} is part of CJ Owl™ Premium Membership. Upgrade to unlock it along with your full Savings Team.
        </p>
        <button
          onClick={openPricing}
          className="mt-5 inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-leaf-500 hover:bg-leaf-600 text-white text-sm font-bold transition-colors"
        >
          <Crown className="w-4 h-4" /> Upgrade to Premium <ArrowRight className="w-4 h-4" />
        </button>

        {/* The way back to the ask after it has been closed. The popup opens by itself the first
            time a locked page is reached, but closing it must not make the question unreachable —
            and reopening it on every navigation would be nagging, which the client ruled out. */}
        {preferencesMissing && (
          <p className="mt-4 text-[12.5px] text-gray-500">
            <button onClick={openPreferences} className="font-semibold text-cj-800 hover:underline">
              Tell us what to look at
            </button>{" "}
            so we can find your savings.
          </p>
        )}
      </div>

      {/* Basic vs Premium comparison */}
      <div className="mt-9 max-w-lg mx-auto">
        <h2 className="text-[15px] font-extrabold text-navy-500 text-center">Compare plans</h2>
        <p className="text-[12.5px] text-gray-400 text-center mt-0.5 mb-4">Everything in Basic, plus the full Premium toolkit.</p>
        <PlanComparison />
        <div className="text-center mt-5">
          <button
            onClick={openPricing}
            className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-xl bg-cj-800 hover:bg-cj-900 text-white text-sm font-bold transition-colors"
          >
            <Crown className="w-4 h-4" /> Upgrade to Premium
          </button>
        </div>
      </div>
    </div>
  );
}
